import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, TrendingUp, DollarSign, AlertTriangle, Clock, ShieldAlert, ArrowUpRight, Store } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import api from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'custom', label: 'Custom' },
];

function KPICard({ label, value, icon: Icon, color, subtitle, onClick }) {
  return (
    <div
      className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Icon LEFT + label in same row — icon standardized to w-10 h-10 / size=20 */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <p className="text-xs text-gray-500 uppercase tracking-wider leading-tight">{label}</p>
      </div>
      {/* Amount as its own dedicated row beneath the icon+label */}
      <p className="font-heading text-2xl font-bold text-gray-800">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      {onClick && (
        <div className="mt-3 flex items-center gap-1 text-xs text-[#561D1A] font-medium">
          View details <ArrowUpRight size={11} />
        </div>
      )}
    </div>
  );
}

function AlertCard({ icon: Icon, color, bgColor, title, count, onClick }) {
  return (
    <div
      className={`${bgColor} rounded-xl p-4 flex items-center gap-4 ${onClick ? 'cursor-pointer hover:opacity-90' : ''} transition-all`}
      onClick={onClick}
      data-testid={`alert-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{title}</p>
        <p className="text-xs text-gray-500">{count} item(s) require attention</p>
      </div>
      {onClick && <ArrowUpRight size={14} className="text-gray-400" />}
    </div>
  );
}

const CHART_COLORS = ['#561D1A', '#EC1A23', '#D4AF37', '#2563eb', '#16a34a'];

export default function DashboardTab({ onNavigate }) {
  const { currency: activeCurrency } = useCurrency();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') {
        if (customFrom) params.set('date_from', customFrom);
        if (customTo) params.set('date_to', customTo);
      }
      const res = await api.get(`/api/store/admin/dashboard?${params}`);
      setStats(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
      </div>
      <div className="h-72 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  if (!stats) return null;
  const { kpis, chart_issued_redeemed, top_stores, alerts } = stats;

  return (
    <div className="space-y-6" data-testid="dashboard-content">
      {/* Time Filter */}
      <div className="flex flex-wrap items-center gap-2" data-testid="time-filter">
        {PERIOD_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setPeriod(opt.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              period === opt.value
                ? 'bg-[#561D1A] text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-[#561D1A]/30'
            }`}
            data-testid={`period-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#561D1A]" data-testid="custom-date-from" />
            <span className="text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#561D1A]" data-testid="custom-date-to" />
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="kpi-cards">
        <KPICard
          label="Issued This Month"
          value={kpis.vouchers_issued_month?.toLocaleString() || '0'}
          icon={Ticket}
          color="bg-[#561D1A]"
          subtitle="Current month"
          onClick={() => onNavigate?.('vouchers')}
        />
        <KPICard
          label="Value Issued"
          value={formatAmount(kpis.total_value_issued || 0, activeCurrency)}
          icon={DollarSign}
          color="bg-[#D4AF37]"
          subtitle={`${kpis.total_vouchers_period || 0} vouchers in period`}
        />
        <KPICard
          label="Value Redeemed"
          value={formatAmount(kpis.total_value_redeemed || 0, activeCurrency)}
          icon={TrendingUp}
          color="bg-[#EC1A23]"
        />
        <KPICard
          label="Outstanding"
          value={formatAmount(kpis.total_outstanding || 0, activeCurrency)}
          icon={DollarSign}
          color="bg-emerald-600"
          subtitle="Active vouchers balance"
        />
        <KPICard
          label="Active Vouchers"
          value={kpis.active_vouchers?.toLocaleString() || '0'}
          icon={Ticket}
          color="bg-blue-600"
          onClick={() => onNavigate?.('vouchers', { status: 'active' })}
        />
      </div>

      {/* Alerts */}
      {(alerts.expiring_soon > 0 || alerts.suspicious_activity > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="alerts-section">
          {alerts.expiring_soon > 0 && (
            <AlertCard
              icon={Clock}
              color="bg-amber-500"
              bgColor="bg-amber-50"
              title="Vouchers Expiring Soon"
              count={alerts.expiring_soon}
              onClick={() => onNavigate?.('vouchers', { status: 'active' })}
            />
          )}
          {alerts.suspicious_activity > 0 && (
            <AlertCard
              icon={ShieldAlert}
              color="bg-red-500"
              bgColor="bg-red-50"
              title="Suspicious Activity"
              count={alerts.suspicious_activity}
            />
          )}
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Issued vs Redeemed */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="chart-issued-redeemed">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 uppercase tracking-wider">Issued vs Redeemed</h3>
          {chart_issued_redeemed?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart_issued_redeemed} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} />
                <YAxis tick={{ fontSize: 11, fill: '#999' }} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 12, border: '1px solid #e5e5e5' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="issued" name="Issued" fill="#561D1A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="redeemed" name="Redeemed" fill="#EC1A23" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              No data for selected period
            </div>
          )}
        </div>

        {/* Top 5 Stores */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="chart-top-stores">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 uppercase tracking-wider">Top Stores by Redemption</h3>
          {top_stores?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={top_stores}
                    dataKey="total_redeemed"
                    nameKey="store_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={40}
                  >
                    {top_stores.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatAmount(v, activeCurrency)} contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {top_stores.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="text-gray-700 truncate flex-1">{s.store_name}</span>
                    <span className="text-gray-500 font-mono">{formatAmount(s.total_redeemed, activeCurrency)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center">
                <Store size={24} className="mx-auto mb-2 text-gray-300" />
                <p>No redemption data yet</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Active Stores" value={kpis.total_stores?.toLocaleString() || '0'} icon={Store} color="bg-[#561D1A]" />
        <KPICard label="Shoppers" value={kpis.total_shoppers?.toLocaleString() || '0'} icon={AlertTriangle} color="bg-purple-500" />
      </div>
    </div>
  );
}
